import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Switch, Route, Redirect, useLocation, useHistory, matchPath } from "react-router-dom";
import { createCustomTheme } from "./theme";
import { THEMES } from "./theme/constants";
import useSettings from "./hooks/useSettings";
import { ThemeProvider, CssBaseline, Dialog, Box } from "@mui/material";
import ErrorBoundary from "./errorBoundary/errorBoundary";
import Loading from "./components/loaders/Loading";
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { getField, getTranslation, handleNotificationClick } from "./utils/appBootUtils";
import {
  appendAdminStockLoaderScriptIfNeeded,
  appendOperationsStockLoaderScriptIfNeeded,
  hasActiveStockMfePage,
  isMemoryConstrainedDevice,
} from "./utils/stockMfePreload";
import rtlCache from './utils/rltCache';
import { toast, ToastContainer } from 'react-toastify';
import { setSupportedLanguages } from "./utils/languageService";
import NavigationHandler from "./utils/NavigateTo";
import { ROUTE_PREFIX, stripShellPrefix } from "./config/routePrefixes";
import { hasBootstrappedBefore, markBootstrapped } from "./utils/appBootFlag";
import { isNativeApp } from "./native/platform";
import { isEmbedded } from "./utils/embed";
import { getTabRestaurant } from './utils/tabRestaurant';

/* Authenticated shell is a separate chunk (Sidebar, Header, route registry). */
const DefaultLayout = lazy(() => import("./layouts/defaultLayout/DefaultLayout"));
const ErrorPage = lazy(() => import("./pages/error/ErrorPage"));
const ForbiddenPage = lazy(() => import("./pages/error/Forbidden"));
const OfflinePage = lazy(() => import("./pages/error/OfflinePage"));
const LogIn = lazy(() => import("./pages/auth/Login"));
/* AI assistant: floating button + chat, on every signed-in page. Own chunk. */
const AiAgent = lazy(() => import("./components/aiAgent/AiAgent"));
const GlobalSearch = lazy(() => import("./components/globalSearch/GlobalSearch"));
// Android / iOS app only (loaded there only): swipe down from the top to refresh.
const NativePullToRefresh = lazy(() => import("./native/NativePullToRefresh"));
const AI_AGENT_HIDDEN_PATHS = ["/login", "/not-found", "/forbidden"];

// Expose toast function to child components (e.g., stock app)
if (typeof window !== 'undefined') {
  window.__PARENT_TOAST__ = toast;
}
const reformat = (values) => {
  let final = []
  values?.map((page) => {
    final.push({ name: page.name, route: page.route, isActive: page.isActive, key: page.key })
    if (page.subPage?.length > 0)
      final.push(...reformat(page.subPage))
  })
  return final
}

// rtl-ltr
const ltrCache = createCache({ key: 'mui-ltr' });

const getDirection = (supportedLanguages, language) => {
  const index = supportedLanguages.find(lang => lang.name.toLowerCase() === language.toLowerCase());
  return index ? index.direction : "ltr"; // Default ltr if not found
};

export const App = () => {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const selectedRestaurant = useSelector((state) => state.rest.selectedRest);
  const selectedBranch = useSelector((state) => state.branch.selectedBranch);
  const branches = useSelector((state) => state.branch.branches);
  const permissions = useSelector((state) => state.auth.permissions);
  const permissionsOperation = useSelector((state) => state.auth.permissionsOperation);
  const language = useSelector((state) => state.translation.language);
  const translation = useSelector((state) => state.translation.translation);
  const loading = useSelector(
    (state) => state.translation.loading || state.rest.loading || state.translation.supportedLanguagesLoading
  );
  // False until GET /language settles. Unlike `loading`, this gate is NOT bypassed by silentReload:
  // nothing may render before the dictionary is in, otherwise the first paint shows raw keys.
  const translationReady = useSelector((state) => state.translation.translationReady);
  const fcmToken = useSelector((state) => state.notifications.token);
  const restaurants = useSelector((state) => state.rest.restaurants);
  const themePreference = useSelector((state) => state.layout.theme);
  const admin = useSelector((state) => state.auth.admin);
  const supportedLanguages = useSelector((state) => state.translation.supportedLanguages);
  const supportedLanguagesWithCode = useSelector((state) => state.translation.supportedLanguagesWithCode);
  const [direction, setDirection] = useState("ltr");
  const [blockGetBranches, setBlockGetBranches] = useState(true)
  const location = useLocation();
  const history = useHistory()
  const getBranches = useCallback(async (restaurantUuid) => {
    const actions = (await import("./redux/actions/BranchActions")).default;
    dispatch(actions.getBranches(restaurantUuid));
  }, [dispatch]);
  const getRest = useCallback(async (restaurantUuid) => {
    const actions = (await import("./redux/actions/RestActions")).default;
    dispatch(actions.getRest(restaurantUuid, getBranches, setBlockGetBranches, history));
  }, [dispatch, getBranches, history]);
  const getEntities = useCallback(async () => {
    const actions = (await import("./redux/actions/EntityActions")).default;
    dispatch(actions.getEntities());
  }, [dispatch]);
  const getGlobalConfig = useCallback(async () => {
    const actions = (await import("./redux/actions/GlobalActions")).default;
    dispatch(actions.getGlobalConfig());
  }, [dispatch]);
  const getTranslations = useCallback(async () => {
    try {
      const actions = (await import("./redux/actions/TranslationsActions")).default;
      dispatch(actions.getTranslation());
    } catch (e) {
      // Chunk failed to load (flaky network / stale deploy): the request never reaches the reducer,
      // so open the boot gate here instead of leaving the app stuck on the loader. Literal type —
      // importing the constant would statically pull the chunk this file deliberately splits out.
      dispatch({ type: "GET_TRANSLATION_ERROR" });
    }
  }, [dispatch]);
  const getSupportedLanguages = useCallback(async () => {
    const actions = (await import("./redux/actions/TranslationsActions")).default;
    dispatch(actions.getSupportedLanguages());
  }, [dispatch]);
  const setSelectedBranch = useCallback(async (branchUuid) => {
    const actions = (await import("./redux/actions/BranchActions")).default;
    dispatch(actions.setSelectedBranch(branchUuid));
  }, [dispatch]);
  const getModules = useCallback(async () => {
    const actions = (await import("./redux/actions/ModulesActions")).default;
    dispatch(actions.getModules());
  }, [dispatch]);
  const getMe = useCallback(async () => {
    const actions = (await import("./redux/actions/AuthActions")).default;
    dispatch(actions.getMe());
  }, [dispatch]);
  const setFcmToken = useCallback(async (token) => {
    const actions = (await import("./redux/actions/NotificationsActions")).default;
    dispatch(actions.setFcmToken(token));
  }, [dispatch]);
  const registerDevice = useCallback(async (token) => {
    const actions = (await import("./redux/actions/NotificationsActions")).default;
    dispatch(actions.registerDevice(token));
  }, [dispatch]);
  const getNotifications = useCallback(async () => {
    const actions = (await import("./redux/actions/NotificationsActions")).default;
    dispatch(actions.getNotifications());
  }, [dispatch]);
  const getChatMessages = useCallback(async (ruuid, bruuid, chatId, pageLimit, page, disableLoading) => {
    const actions = (await import("./redux/actions/MessagesActions")).default;
    dispatch(actions.getChatMessages(ruuid, bruuid, chatId, pageLimit, page, disableLoading));
  }, [dispatch]);
  const setSelectedRest = useCallback(async (value) => {
    const actions = (await import("./redux/actions/RestActions")).default;
    dispatch(actions.setSelectedRest(value));
  }, [dispatch]);
  const getUserGlobalChats = useCallback(async (isSilent) => {
    const actions = (await import("./redux/actions/MessagesActions")).default;
    dispatch(actions.getUserGlobalChats(isSilent));
  }, [dispatch]);
  const setSelectedRestData = useCallback(async (data) => {
    const actions = (await import("./redux/actions/RestActions")).default;
    dispatch(actions.setSelectedRestData(data));
  }, [dispatch]);
  const getAutomaticTranslationState = useCallback(async () => {
    const actions = (await import("./redux/actions/TranslationsActions")).default;
    dispatch(actions.getAutomaticTranslationState());
  }, [dispatch]);
  const setNotificationOpen = useCallback(async (state) => {
    const actions = (await import("./redux/actions/LayoutActions")).default;
    dispatch(actions.setNotificationOpen(state));
  }, [dispatch]);
  const setNotifications = useCallback(async (notification) => {
    const actions = (await import("./redux/actions/NotificationsActions")).default;
    dispatch(actions.setNotifications(notification));
  }, [dispatch]);
  const collapseSidebar = useCallback(async () => {
    const actions = (await import("./redux/actions/LayoutActions")).default;
    dispatch(actions.allNavChange(false));
  }, [dispatch]);
  // A page refresh resets Redux to its initial "loading" state even though the tab already
  // bootstrapped once — skip the full-screen Loading gate on those silent reloads so the
  // header/layout stay mounted while data refetches in the background.
  const [silentReload] = useState(hasBootstrappedBefore);

  useEffect(() => {
    if (!loading) markBootstrapped();
  }, [loading]);

  // The sidebar's pinned-open flag lives in the persisted (IndexedDB) redux state, which is
  // shared across every tab of this origin — so a freshly opened tab rehydrates the menu as
  // "open". Collapse it on the first bootstrap of each tab (sessionStorage is per-tab, so a
  // new tab reads silentReload=false); a plain reload of an already-bootstrapped tab keeps
  // the flag set and leaves the menu as the user left it. PersistGate delays this mount until
  // after rehydration, so the dispatch reliably wins.
  useEffect(() => {
    if (!silentReload) collapseSidebar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let dir = getDirection(supportedLanguages, language) || 'ltr';
    setDirection(dir);

    if (dir === "rtl") {

      document.documentElement.setAttribute("dir", "rtl");
    } else {

      document.documentElement.setAttribute("dir", "ltr");
    }
  }, [language, supportedLanguages]);

  useEffect(() => {
    const languages = supportedLanguages?.length > 0
      ? supportedLanguages
      : supportedLanguagesWithCode?.length > 0
        ? supportedLanguagesWithCode
        : null;

    if (languages) {
      window.__SHARED_SUPPORTEDLANGS__ = languages;
      setSupportedLanguages(languages);
    }
  }, [supportedLanguages, supportedLanguagesWithCode]);
  // // Load theme from user settings on mount
  // useEffect(() => {
  //   if (accessToken && admin?.settings?.theme) {
  //     setTheme(admin.settings.theme);
  //   } else if (!themePreference) {
  //     // Default to 'system' if no theme is set
  //     setTheme('system');
  //   }
  // }, [accessToken, admin?.settings?.theme, setTheme, themePreference]);

  useEffect(() => {
    if (accessToken) {
      getMe()
      // prefer this tab's own last choice over the value shared by every tab through redux-persist
      getRest(getTabRestaurant() || selectedRestaurant);
      getEntities();
    }
  }, [accessToken]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const updateOnlineStatus = async () => {
    const onlineStatus = await checkInternetConnection();
    setIsOnline(onlineStatus);
  };

  const checkInternetConnection = async () => {
    // Previously also pinged https://www.google.com as a stronger reachability
    // check, but that request used `mode: 'no-cors'`, which always yields an
    // opaque response (`response.ok` is always false) — so `googleOn` could
    // never become true and the function always returned `navigator.onLine`
    // regardless of the fetch's outcome. It cost a third-party network round
    // trip on every page load and every 5-minute poll for zero effect on the
    // result, so it's removed rather than "fixed" into a real cross-origin
    // check the app doesn't otherwise need.
    return navigator.onLine
  };

  useEffect(() => {
    updateOnlineStatus();

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    const intervalId = setInterval(() => {
      updateOnlineStatus();
    }, 300000); // 300000 ms = 5 minutes

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    let data = restaurants.find((rest) => rest.uuid === selectedRestaurant)
    if (data)
      setSelectedRestData(data)
  }, [selectedRestaurant])
  const isFirstRender = useRef(true)
  /** Previous render: were we under the `/operations` shell (embedded stock MFE)? */
  const wasOnOperationsShellRef = useRef(null)
  /** Previous `location.pathname` (for skipping duplicate getRest on `/login` → `/operations`). */
  const prevPathnameForOpsShellRef = useRef(null)

  /**
   * Refresh restaurants + permission trees when entering `/operations` from elsewhere.
   * `getRest` otherwise only runs on login (`accessToken`); without this, staying in stock then
   * switching to admin and back was the only way to pick up server-side role changes because
   * remounting `<operations-stock-app>` re-hydrated from an already-updated store after a manual visit.
   */
  useEffect(() => {
    if (!accessToken) return
    const p = location.pathname || ''
    const prevPath = prevPathnameForOpsShellRef.current
    const onOperations =
      p === ROUTE_PREFIX.OPERATIONS || p.startsWith(`${ROUTE_PREFIX.OPERATIONS}/`)
    const prevOnOperations = wasOnOperationsShellRef.current

    if (prevOnOperations === null) {
      wasOnOperationsShellRef.current = onOperations
      prevPathnameForOpsShellRef.current = p
      return
    }

    const enteredOperations = onOperations && !prevOnOperations
    wasOnOperationsShellRef.current = onOperations
    prevPathnameForOpsShellRef.current = p

    if (!enteredOperations) return
    if (typeof prevPath === 'string' && prevPath.startsWith('/login')) return
    getRest(selectedRestaurant)
  }, [
    accessToken,
    location.pathname,
    selectedRestaurant,
    getRest,
  ])

  // Title lookup uses the pages of the shell the URL is in: /admin and /operations reuse routes (e.g. /dashboard).
  const onOperationsShell =
    location.pathname === ROUTE_PREFIX.OPERATIONS || location.pathname.startsWith(`${ROUTE_PREFIX.OPERATIONS}/`)
  const shellPermissions = onOperationsShell ? permissionsOperation : permissions
  const allPages = useMemo(
    () => reformat(Array.isArray(shellPermissions) ? shellPermissions : []).filter((data) => data.route && data.isActive),
    [shellPermissions]
  )
  const { settings } = useSettings();

  // Get effective theme (handle 'system' by detecting browser preference)
  const getEffectiveTheme = () => {
    if (themePreference === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT;
    }
    return themePreference === 'dark' ? THEMES.DARK : THEMES.LIGHT;
  };

  const [effectiveTheme, setEffectiveTheme] = useState(getEffectiveTheme());

  // Update effective theme when Redux theme changes or browser preference changes
  useEffect(() => {
    const updateTheme = () => {
      setEffectiveTheme(getEffectiveTheme());
    };

    updateTheme();

    // Listen to browser preference changes when theme is 'system'
    if (themePreference === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        updateTheme();
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
      // Fallback for older browsers
      else if (mediaQuery.addListener) {
        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
      }
    }
  }, [themePreference]);

  const theme = createCustomTheme({
    direction: direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    stableFieldErrors: settings.stableFieldErrors,
    theme: effectiveTheme,
  });

  // Determine if theme is dark
  const isDarkMode = theme.palette.mode === 'dark';

  useEffect(() => {
    let cancelled = false;

    const refreshFcmToken = async () => {
      try {
        const { messenger, requestNotificationPermission } = await import('./firebase');
        if (cancelled || !messenger) return;

        try {
          const { deleteToken } = await import('firebase/messaging');
          await deleteToken(messenger);
        } catch (error) {
          // No existing token to delete — safe to ignore
        }

        if (!accessToken || cancelled) return;

        const token = await requestNotificationPermission();
        if (token && !cancelled) {
          setFcmToken(token);
        }
      } catch (error) {
        console.warn('FCM setup skipped:', error?.message || error);
      }
    };

    refreshFcmToken();
    return () => {
      cancelled = true;
    };
  }, [accessToken, setFcmToken]);

  useEffect(() => {
    if (accessToken && fcmToken) { registerDevice(fcmToken) }
  }, [fcmToken, accessToken, registerDevice])

  const [notification, setNotification] = useState()

  useEffect(() => {
    let cancelled = false;

    const attachForegroundListener = async () => {
      try {
        const { messenger, onMessageListener } = await import('./firebase');
        if (cancelled || !messenger) return;

        onMessageListener()
          .then((payload) => {
            if (cancelled) return;
            import("./config/config").then(({ confirmNotification }) => {
              confirmNotification(payload.data.uuid);
            });
            setNotification(payload)
            const nextNotification = {
              uuid: payload.data.uuid,
              title: payload.data.title,
              description: payload.data.body || '',
              imageUrl: payload.data.imageUrl || '',
              clickAction: payload.data.clickAction,
              createdAt: new Date().getTime(),
              seenAt: null,
              data: payload.data,
              page: payload.data.page
            };
            if (payload.data.clickAction !== 'view-new-notifications') {
              setNotifications(nextNotification)
            }

            let tmp = stripShellPrefix(window.location.pathname).split('/')
            let tmp2 = payload.data.clickAction.split('/')

            if (tmp[1] === 'messaging' && tmp2[3] === 'messaging') {
              if (tmp[4] !== tmp2[6]) {
                getUserGlobalChats(true)
              }
              if (tmp[2] === tmp2[4] && tmp[3] === tmp2[5] && tmp[4] === tmp2[6])
                getChatMessages(tmp[2], tmp[3], tmp[4], 10, 1, true)
            }
          })
          .catch((err) => {
            if (err.message && err.message.includes('not supported')) {
              return;
            }
            console.log('failed: ', err);
          });
      } catch (error) {
        console.warn('FCM foreground listener skipped:', error?.message || error);
      }
    };

    attachForegroundListener();
    return () => {
      cancelled = true;
    };
  }, [getChatMessages, getUserGlobalChats, setNotifications]);

  useEffect(() => {
    if (notification) {
      toast(
        <Box onClick={() => {
          handleNotificationClick(
            notification.data.clickAction,
            setSelectedRest,
            history,
            setNotificationOpen
          );
        }}>
          {notification.data.title}
        </Box>, {
        hideProgressBar: true
      })
    }
  }, [notification])

  useEffect(() => {
    if (accessToken) {
      getNotifications()
    }
  }, [accessToken])

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined
    // Removed on cleanup: before, every re-run stacked another listener, so one notification click
    // could fire setSelectedRest several times (and switch restaurant "on its own").
    const onServiceWorkerMessage = (event) => {
          if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
            // Use handleNotificationClick to properly set restaurant and navigate
            if (event.data.clickAction) {
              handleNotificationClick(
                event.data.clickAction,
                setSelectedRest,
                history,
                setNotificationOpen
              );
            } else if (event.data.url && event.data.restaurantId) {
              // Fallback: if clickAction is not available, use url and restaurantId
              setSelectedRest(event.data.restaurantId);
              setTimeout(() => {
                history.push(event.data.url);
              }, 100);
            }
          }
    };
    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
  }, [dispatch, history, setSelectedRest, setNotificationOpen]);

  useEffect(() => {
    const handleMessage = (event) => {

      if (event.data && event.data.type === 'NEW_NOTIFICATION' && event.data.clickAction !== 'view-new-notifications') {
        // Format the notification data
        const notification = {
          uuid: event.data.notification.uuid,
          title: event.data.notification.title,
          description: event.data.notification.body || '',
          imageUrl: event.data.notification.imageUrl || '',
          clickAction: event.data.notification.clickAction,
          createdAt: new Date().getTime(),
          seenAt: null,
          page: event.data.notification.page,
          data: event.data.notification
        };
        setNotifications(notification);
      }
    };

    // Make sure navigator.serviceWorker exists before adding listener
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    } else {
      console.log('Service worker not available or not controlling the page');
    }

    return () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, [dispatch]);

  useEffect(() => {
    getGlobalConfig();
    getTranslations();
    getModules();
    getSupportedLanguages();
  }, []);

  useEffect(() => {
    if (accessToken) {
      getAutomaticTranslationState();
    }
  }, [accessToken, getAutomaticTranslationState]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (accessToken && selectedRestaurant) {
      // clearAllBranches();
      getBranches(selectedRestaurant);
    }
  }, [selectedRestaurant]);

  useEffect(() => {
    if (
      selectedRestaurant &&
      branches?.length > 0 &&
      (!selectedBranch ||
        !branches?.find((branch) => {
          return branch?.uuid === selectedBranch;
        }))
    ) {
      setSelectedBranch(branches[0]?.uuid);
    }
  }, [branches, selectedRestaurant]);

  const pagesDictionary = getTranslation('page-names', translation)

  useEffect(() => {
    const pathForTitle = stripShellPrefix(location.pathname) || location.pathname
    // exact route, then a route with params (/messaging/:ruuid/…), then the closest parent page (branch edit → Branches)
    const selectedPage =
      allPages.find((page) => page.route === pathForTitle) ||
      allPages.find((page) => page.route.includes('/:') && matchPath(pathForTitle, { path: page.route, exact: true })) ||
      allPages
        .filter((page) => page.route !== '/' && pathForTitle.startsWith(`${page.route}/`))
        .sort((a, b) => b.route.length - a.route.length)[0]
    // same name as public/index.html, so the title doesn't flip on load
    document.title = selectedPage ? getField(pagesDictionary, selectedPage.key, language, 'Cloudix') : 'Cloudix'
  }, [location.pathname, allPages, translation, language]);

  // Preload stock MFE scripts during idle time so Stock Admin / Operations open without waiting on the loader.
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    let idleId;
    let timeoutId;
    const run = () => {
      if (cancelled) return;
      // On iOS Safari / low-RAM devices, running all three app runtimes at once
      // exceeds the tab memory budget and crash-reloads the page — load on demand there.
      if (isMemoryConstrainedDevice()) return;
      if (hasActiveStockMfePage(permissions)) {
        appendAdminStockLoaderScriptIfNeeded();
      }
      // Operations shell is always the stock MFE; preload whenever this user has any operations menu.
      if (permissionsOperation?.length > 0) {
        appendOperationsStockLoaderScriptIfNeeded();
      }
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 4000 });
    } else if (typeof window !== 'undefined') {
      timeoutId = window.setTimeout(run, 1);
    }
    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
    };
  }, [accessToken, permissions, permissionsOperation]);

  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <ErrorBoundary>
          <NavigationHandler />
          <Dialog open={!isOnline} fullScreen>
            <Suspense fallback={<Loading fullScreen />}>
              <OfflinePage />
            </Suspense>
          </Dialog>
          {!translationReady || (loading && !silentReload) ? (
            <Loading fullScreen />
          ) : (
            <Suspense fallback={silentReload ? null : <Loading fullScreen />}>
              <Switch>
                <Route path="/not-found" component={ErrorPage} />
                <Route path="/forbidden" component={ForbiddenPage} />
                {/*ErrorPage*/}
                {/* <Route exact path="/error">
                    <ErrorPage />
                  </Route> */}

                {/* Login and Signup*/}
                <Route exact path="/login" component={LogIn} />
                {/* <Route exact path="/forgot-password" component={ForgotPassword} /> */}

                {/* Main Content: /admin = admin app, /operations = operations (e.g. stock MFE); login stays at /login */}
                {accessToken ? (
                  <>
                    <Route
                      path="/admin"
                      render={(props) => (
                        <DefaultLayout {...props} routePrefix={ROUTE_PREFIX.ADMIN} />
                      )}
                    />
                    <Route
                      path="/operations"
                      render={(props) => (
                        <DefaultLayout {...props} routePrefix={ROUTE_PREFIX.OPERATIONS} />
                      )}
                    />
                  </>
                ) : (
                  <Redirect to="/login" />
                )}
                <Redirect from="*" to="/not-found" />
                {/* <Route>
                    <Redirect to="/error" />
                  </Route> */}
              </Switch>
            </Suspense>
          )}
          {accessToken && translationReady && !AI_AGENT_HIDDEN_PATHS.some((p) => location.pathname.startsWith(p)) && (
            <Suspense fallback={null}>
              <AiAgent />
              {/* Header search palette (Ctrl/⌘ K or any header's search box) — admin and the embedded stock apps. */}
              {!isEmbedded() && <GlobalSearch />}
            </Suspense>
          )}
          <ToastContainerWrapper theme={theme} isDarkMode={isDarkMode} direction={direction} />
          {isNativeApp() && (
            <Suspense fallback={null}>
              <NativePullToRefresh />
            </Suspense>
          )}
        </ErrorBoundary>
      </ThemeProvider>
    </CacheProvider>
  );
};

// ToastContainer wrapper component to apply theme styles
const ToastContainerWrapper = ({ theme, isDarkMode, direction }) => {
  React.useEffect(() => {
    // Apply CSS custom properties for react-toastify
    const root = document.documentElement;
    // Use background.default for light mode (more solid), background.paper for dark mode
    const isLight = theme.palette.mode === 'light';
    const toastBackground = isLight
      ? (theme.palette.background.default || '#f4f5f7')
      : (theme.palette.background.paper || '#222b36');

    root.style.setProperty('--toastify-color-light', toastBackground);
    root.style.setProperty('--toastify-color-dark', toastBackground);
    root.style.setProperty('--toastify-text-color-light', theme.palette.text.primary);
    root.style.setProperty('--toastify-text-color-dark', theme.palette.text.primary);
    root.style.setProperty('--toastify-color-info', theme.palette.info?.main || '#2196f3');
    root.style.setProperty('--toastify-color-success', theme.palette.success.main);
    root.style.setProperty('--toastify-color-warning', theme.palette.warning.main);
    root.style.setProperty('--toastify-color-error', theme.palette.error.main);
    root.style.setProperty('--toastify-icon-color-info', theme.palette.info?.main || '#2196f3');
    root.style.setProperty('--toastify-icon-color-success', theme.palette.success.main);
    root.style.setProperty('--toastify-icon-color-warning', theme.palette.warning.main);
    root.style.setProperty('--toastify-icon-color-error', theme.palette.error.main);
    // Ensure toast background is fully opaque
    root.style.setProperty('--toastify-toast-background', toastBackground);
    // Card hairline border for the compact toast look (style block below).
    root.style.setProperty('--toastify-toast-border', theme.palette.divider);
  }, [theme]);

  return (
    <>
      {/* Compact accent card look: paper background, hairline border, semantic
          accent edge (inline-start so RTL flips it), slim progress bar. Serves
          the shell AND the embedded stock apps (they delegate via __PARENT_TOAST__). */}
      <style>{`
        .Toastify__toast-container {
          width: 300px;
          padding: 0;
        }
        .Toastify__toast {
          opacity: 1 !important;
          background-color: var(--toastify-color-light) !important;
          min-height: 44px;
          margin-bottom: 8px;
          padding: 8px 12px;
          border-radius: 10px;
          border: 1px solid var(--toastify-toast-border, rgba(145, 158, 171, 0.24));
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
          font-size: 13px;
          align-items: center;
          /* Guard against v8/v9 toastify CSS leaking from embedded MFE bundles:
             their base rule is justify-content: space-between, which shoves the
             icon and label to opposite ends on v11 (no toast-body wrapper). */
          justify-content: flex-start !important;
        }
        .Toastify__toast--dark {
          background-color: var(--toastify-color-dark) !important;
        }
        .Toastify__toast-body {
          background-color: transparent !important;
          margin: 0;
          padding: 0 4px;
          line-height: 1.4;
        }
        .Toastify__toast-icon {
          width: 18px;
          margin-inline-end: 10px;
        }
        .Toastify__toast--success { border-inline-start: 3px solid var(--toastify-color-success); }
        .Toastify__toast--error   { border-inline-start: 3px solid var(--toastify-color-error); }
        .Toastify__toast--warning { border-inline-start: 3px solid var(--toastify-color-warning); }
        .Toastify__toast--info    { border-inline-start: 3px solid var(--toastify-color-info); }
        .Toastify__close-button {
          align-self: center;
          flex-shrink: 0;
          opacity: 0.6;
        }
        .Toastify__close-button > svg {
          width: 14px;
          height: 14px;
        }
        .Toastify__progress-bar {
          height: 2px;
        }
      `}</style>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={direction === 'rtl'}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </>
  );
};

export default App;
